import { useLayoutEffect, useRef, useState } from "react";
import { Lock, Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { formatMoneyShort } from "@/components/Plan/MoneyInput";
import { PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import ProductGroup from "./ProductGroup";
import {
  isLocked,
  monthLabel,
  revenueTotal,
  type CustomerInfo,
  type ForecastProduct,
} from "../lib/forecastGrid";

interface Props {
  months: string[];
  lockedThrough: string;
  products: ForecastProduct[];
  currency?: string | null;
  canEdit: boolean;
  currentUserId: string | null;
  customers: CustomerInfo[];
  onCellSave: (customerId: string, productExternalId: string, month: string, units: string) => void;
  onAddCustomer: (productExternalId: string, customerId: string) => void;
  onAddProduct?: () => void;
}

const MONTH_COL_MIN_PX = 88;

// Compact money so 12 columns of monthly revenue fit; "—" when nothing is priced.
function revenue(products: ForecastProduct[], month: string, currency?: string | null): string {
  const total = revenueTotal(products, month);
  return total == null ? "—" : formatMoneyShort(total, currency);
}

// Spreadsheet-style grid mirroring the Bottom Up monthly grid: scrolls with the
// page, sticky two-row header (months + grand totals), sticky first
// column, and locked past months shaded grey. Renders even with no products
// so the page always offers a way in (inline "Add product").
export default function ForecastGrid({
  months,
  lockedThrough,
  products,
  currency,
  canEdit,
  currentUserId,
  customers,
  onCellSave,
  onAddCustomer,
  onAddProduct,
}: Props) {
  const headRow1Ref = useRef<HTMLTableRowElement>(null);
  const [row2Top, setRow2Top] = useState(0);

  useLayoutEffect(() => {
    const recompute = () => {
      if (headRow1Ref.current) {
        // Minus 1px so the second row overlaps the first instead of leaving a
        // sub-pixel seam that shows body content through.
        setRow2Top(Math.max(0, headRow1Ref.current.getBoundingClientRect().height - 1));
      }
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, []);

  const colSpan = 1 + months.length;
  const tableMinWidth = PRODUCT_COL_WIDTH + months.length * MONTH_COL_MIN_PX;

  return (
    // overflow-clip rounds the corners without making the card a scroll box,
    // so the page itself scrolls and the headers stick to it. The card grows
    // with the table so narrow screens scroll the page sideways.
    <Card className="overflow-clip" style={{ minWidth: tableMinWidth }}>
      <table
        className="border-collapse text-xs"
        style={{
          tableLayout: "fixed",
          width: "100%",
        }}
      >
        <colgroup>
          <col style={{ width: PRODUCT_COL_WIDTH }} />
          {months.map((m) => (
            <col key={m} />
          ))}
        </colgroup>
        <thead>
          <tr ref={headRow1Ref} style={{ borderColor: "var(--border)" }} className="border-b">
            <th
              style={{ background: "var(--muted)", color: "var(--muted-foreground)", borderColor: "var(--border)", top: 0 }}
              className="sticky left-0 z-50 border-r px-3 py-2 text-left font-semibold whitespace-nowrap"
            >
              Product / Customer
            </th>
            {months.map((month) => {
              const locked = isLocked(month, lockedThrough);
              return (
                <th
                  key={month}
                  style={{
                    background: locked ? "var(--border)" : "var(--muted)",
                    color: "var(--muted-foreground)",
                    top: 0,
                  }}
                  className="sticky z-40 px-2 py-2 text-right font-semibold whitespace-nowrap"
                >
                  {locked && <Lock size={10} className="mr-1 inline-block align-[-1px]" aria-label="Locked" />}
                  {monthLabel(month)}
                </th>
              );
            })}
          </tr>
          <tr style={{ borderColor: "var(--border)" }} className="border-b">
            <th
              style={{ background: "var(--muted)", color: "var(--foreground)", borderColor: "var(--border)", top: row2Top }}
              className="sticky left-0 z-50 border-r px-3 py-2 text-left font-semibold whitespace-nowrap"
            >
              Revenue
            </th>
            {months.map((month) => {
              const locked = isLocked(month, lockedThrough);
              return (
                <th
                  key={month}
                  style={{
                    background: locked ? "var(--border)" : "var(--muted)",
                    color: "var(--foreground)",
                    top: row2Top,
                  }}
                  className="sticky z-40 px-2 py-2 text-right font-semibold tabular-nums whitespace-nowrap"
                >
                  {revenue(products, month, currency)}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {products.map((product) => (
            <ProductGroup
              key={product.external_id}
              product={product}
              months={months}
              lockedThrough={lockedThrough}
              canEdit={canEdit}
              currentUserId={currentUserId}
              customers={customers}
              onCellSave={onCellSave}
              onAddCustomer={onAddCustomer}
            />
          ))}

          {products.length === 0 && (
            <tr>
              <td colSpan={colSpan} className="px-4 pt-6 text-xs text-muted-foreground">
                No forecasts yet — add a product to start entering numbers.
              </td>
            </tr>
          )}

          {canEdit && onAddProduct && (
            <tr style={{ borderColor: "var(--accent)" }} className="border-b">
              <td
                style={{ background: "var(--background)", borderColor: "var(--border)" }}
                className="sticky left-0 z-10 border-r px-3 py-1"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onAddProduct}
                  className="h-7 gap-1 px-2 font-normal text-muted-foreground hover:text-foreground"
                >
                  <Plus size={12} />
                  Add product
                </Button>
              </td>
              <td colSpan={months.length} style={{ background: "var(--background)" }} />
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}
